import React, { useState, useEffect, useMemo } from 'react';
import courses from '../courses.json';
import extraOccupied from '../extra_occupied.json';
import './EmptyLectureHalls.css';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function groupHalls(halls) {
    // Group by building prefix (e.g. "LH 108" -> "LH")
    const groups = {};
    halls.forEach((h) => {
        const match = h.match(/^(\w+)\s*(.*)/);
        const key = match ? match[1] : 'Other';
        if (!groups[key]) groups[key] = [];
        groups[key].push(h);
    });
    Object.values(groups).forEach((arr) =>
        arr.sort((a, b) => {
            const na = parseFloat(a.replace(/[^\d.]/g, '')) || 0;
            const nb = parseFloat(b.replace(/[^\d.]/g, '')) || 0;
            return na - nb;
        })
    );
    return groups;
}

const EmptyLectureHalls = () => {
    const [currentTime, setCurrentTime] = useState(new Date());
    const [isCustomTime, setIsCustomTime] = useState(false);

    useEffect(() => {
        const timer = setInterval(() => {
            if (!isCustomTime) setCurrentTime(new Date());
        }, 60000);
        return () => clearInterval(timer);
    }, [isCustomTime]);

    const { emptyHalls, allHalls, occupiedCount } = useMemo(() => {
        const now = currentTime;
        const currentDay = now.getDay();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        const currentTimeValue = currentHour * 100 + currentMinute;

        const allHalls = new Set();
        courses.forEach((course) => {
            if (course.lectureHall) {
                const halls = course.lectureHall.split(',').map((h) => h.trim());
                halls.forEach((hall) => {
                    if (hall.startsWith('LH')) allHalls.add(hall);
                });
            }
        });

        const occupiedHalls = new Set();
        courses.forEach((course) => {
            if (course.slot && course.slot.lectureTiming) {
                const timings = course.slot.lectureTiming.split(',');
                timings.forEach((timing) => {
                    if (timing.length === 9) {
                        const day = parseInt(timing.substring(0, 1), 10);
                        const start = parseInt(timing.substring(1, 5), 10);
                        const end = parseInt(timing.substring(5, 9), 10);
                        if (day === currentDay && currentTimeValue >= start && currentTimeValue < end) {
                            if (course.lectureHall) {
                                const halls = course.lectureHall.split(',').map((h) => h.trim());
                                halls.forEach((hall) => {
                                    if (hall.startsWith('LH')) occupiedHalls.add(hall);
                                });
                            }
                        }
                    }
                });
            }
        });

        extraOccupied.forEach((item) => {
            if (item.day === currentDay) {
                const start = parseInt(item.startTime, 10);
                const end = parseInt(item.endTime, 10);
                if (currentTimeValue >= start && currentTimeValue < end) {
                    if (item.lectureHall.startsWith('LH')) occupiedHalls.add(item.lectureHall);
                }
            }
        });

        const freeHalls = Array.from(allHalls)
            .filter((hall) => !occupiedHalls.has(hall))
            .sort();

        return {
            emptyHalls: freeHalls,
            allHalls: Array.from(allHalls),
            occupiedCount: occupiedHalls.size,
        };
    }, [currentTime]);

    const handleTimeChange = (e) => {
        const value = e.target.value;
        if (!value) return;
        const [hours, minutes] = value.split(':');
        const newTime = new Date(currentTime);
        newTime.setHours(parseInt(hours, 10));
        newTime.setMinutes(parseInt(minutes, 10));
        setCurrentTime(newTime);
        setIsCustomTime(true);
    };

    const handleReset = () => {
        setIsCustomTime(false);
        setCurrentTime(new Date());
    };

    const grouped = useMemo(() => groupHalls(emptyHalls), [emptyHalls]);
    const groupKeys = Object.keys(grouped).sort();

    const timeValue = `${String(currentTime.getHours()).padStart(2, '0')}:${String(currentTime.getMinutes()).padStart(2, '0')}`;

    return (
        <div className="eh">
            <header className="eh__head">
                <div className="eh__eyebrow">Right now · live</div>
                <h1 className="eh__title">
                    Free lecture halls, <em>by hour</em>.
                </h1>
                <p className="eh__sub">
                    All LH-prefixed halls cross-referenced against the live timetable. Pick a different time to plan ahead.
                </p>
            </header>

            <div className="eh__controls">
                <div className="eh__control">
                    <label className="field-label" htmlFor="eh-day">Day</label>
                    <div className="eh__day-value mono">{DAY_NAMES[currentTime.getDay()]}</div>
                </div>
                <div className="eh__control">
                    <label className="field-label" htmlFor="eh-time">Time</label>
                    <input
                        id="eh-time"
                        type="time"
                        className="field field--mono"
                        value={timeValue}
                        onChange={handleTimeChange}
                    />
                </div>
                <div className="eh__control eh__control--inline">
                    {isCustomTime ? (
                        <button className="btn btn--sm" onClick={handleReset}>
                            Reset to now
                        </button>
                    ) : (
                        <span className="eh__live">
                            <span className="eh__live-dot" aria-hidden="true" />
                            Updating live
                        </span>
                    )}
                </div>
            </div>

            <div className="eh__stats">
                <div className="eh__stat">
                    <span className="eh__stat-label">Free</span>
                    <span className="eh__stat-value tnum">{emptyHalls.length}</span>
                </div>
                <div className="eh__stat">
                    <span className="eh__stat-label">Occupied</span>
                    <span className="eh__stat-value tnum">{occupiedCount}</span>
                </div>
                <div className="eh__stat">
                    <span className="eh__stat-label">Total tracked</span>
                    <span className="eh__stat-value tnum">{allHalls.length}</span>
                </div>
            </div>

            {emptyHalls.length === 0 ? (
                <div className="empty">
                    <strong>Every tracked hall is occupied</strong>
                    Try a different time, or check back in 30 minutes.
                </div>
            ) : (
                <div className="eh__groups">
                    {groupKeys.map((key) => (
                        <section key={key} className="eh__group">
                            <header className="eh__group-head">
                                <h2 className="eh__group-title">
                                    <span className="mono">{key}</span>
                                    <span className="muted tnum">{grouped[key].length}</span>
                                </h2>
                                <span className="eh__group-rule" aria-hidden="true" />
                            </header>
                            <div className="eh__halls">
                                {grouped[key].map((hall) => (
                                    <div key={hall} className="eh__hall">
                                        {hall}
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
};

export default EmptyLectureHalls;
